import {
  isLoginV2TwoFactorResponse,
  type LoginV2SuccessResponse,
  type LoginV2TotpRequiredResponse,
  type OAuthProvider,
} from '../../interfaces/UserApiService';
import { useAppStore } from '../../store/store';
import { isUnauthorizedHttpError } from '../../utils/httpError';
import {
  getJwtExpiration,
  hasFileAccessScope,
  isJwtExpired,
  isJwtExpiringSoon,
} from '../../utils/jwt';
import {
  parseTotpSetupRequiredError,
  type TotpSetupRequiredError,
} from '../../utils/totpApiErrors';
import { UserService } from '../userApiService';

export class AuthService {
  private static _instance: AuthService | null = null;
  private userService: UserService | null = null;
  private customTokenRefreshPromise: Promise<boolean> | null = null;
  private backgroundRefreshInterval: number | null = null;

  public static getInstance(): AuthService {
    if (!AuthService._instance) {
      AuthService._instance = new AuthService();
    }
    return AuthService._instance;
  }

  private constructor() {
    // Setup tab focus listener for proactive token check
    this.setupTabFocusListener();
    // Setup background periodic refresh
    this.setupBackgroundRefresh();
  }

  /**
   * Setup background periodic token refresh (every 5 minutes)
   * This ensures tokens stay fresh even if the user is inactive on the same page.
   * The interval is intentionally shorter than the 10-minute access-token lifetime so a
   * focused-but-idle tab (e.g. reading a document in the viewer) refreshes before expiry.
   */
  private setupBackgroundRefresh(): void {
    if (typeof window === 'undefined') return;

    // Check and refresh every 5 minutes
    const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

    this.backgroundRefreshInterval = window.setInterval(() => {
      this.handleBackgroundRefresh();
    }, REFRESH_INTERVAL);

    // Clear interval on page unload
    window.addEventListener('beforeunload', () => {
      if (this.backgroundRefreshInterval) {
        clearInterval(this.backgroundRefreshInterval);
      }
    });
  }

  /**
   * Handle background token refresh
   */
  private async handleBackgroundRefresh(): Promise<void> {
    const store = useAppStore.getState();

    if (!store.customTokens) {
      return;
    }

    try {
      await this.checkAndRefreshTokens();
    } catch (error) {
      console.warn('Background token refresh failed:', error);
    }
  }

  /**
   * Setup listener for tab focus to proactively check and refresh tokens
   */
  private setupTabFocusListener(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('focus', () => {
      this.handleTabFocus();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.handleTabFocus();
      }
    });
  }

  /**
   * Handle tab focus event - check tokens and attempt silent refresh if needed
   */
  private async handleTabFocus(): Promise<void> {
    const store = useAppStore.getState();

    if (!store.customTokens) {
      return;
    }

    try {
      if (this.areCustomTokensExpired() || this.areCustomTokensExpiringSoon()) {
        await this.checkAndRefreshTokens();
      }
    } catch (error) {
      console.warn('Error during tab focus token check:', error);
    }
  }

  /**
   * Check if custom tokens are expired
   */
  private areCustomTokensExpired(): boolean {
    const customTokens = useAppStore.getState().customTokens;
    if (!customTokens) return true;

    // File-scoped sessions have no refresh token: the access JWT itself is the clock.
    if (customTokens.token && hasFileAccessScope(customTokens.token)) {
      return isJwtExpired(customTokens.token);
    }

    if (customTokens.rToken && isJwtExpired(customTokens.rToken)) {
      return true;
    }

    return false;
  }

  /**
   * Check if custom tokens will expire soon (within next 5 minutes)
   */
  private areCustomTokensExpiringSoon(): boolean {
    const customTokens = useAppStore.getState().customTokens;
    if (!customTokens) return false;

    const expiryThreshold = 5 * 60; // 5 minutes

    if (customTokens.token && isJwtExpiringSoon(customTokens.token, expiryThreshold)) {
      return true;
    }

    return false;
  }

  /**
   * Check token validity and attempt refresh if needed
   * Handles concurrent calls by reusing in-flight refresh operations
   */
  public async checkAndRefreshTokens(): Promise<boolean> {
    const store = useAppStore.getState();

    if (store.customTokens) {
      // File-scoped OTP sessions must never call /user/auth/rtoken (403 DENIED).
      if (store.customTokens.token && hasFileAccessScope(store.customTokens.token)) {
        return !this.areCustomTokensExpired();
      }

      // Tokens are still valid, no action needed
      if (!this.areCustomTokensExpired() && !this.areCustomTokensExpiringSoon()) {
        return true;
      }

      return this.refreshWithDeduplication();
    }

    return true;
  }

  /**
   * Force a token refresh regardless of the "expiring soon" heuristic.
   *
   * Unlike {@link checkAndRefreshTokens}, this never returns early when the access token
   * still looks locally valid — it is used to recover from clock-drift races where the
   * backend rejects a just-expired access token (401 SESSION_EXPIRED). Concurrent callers
   * share the same in-flight refresh via {@link refreshWithDeduplication}, so parallel 401s
   * do not launch parallel refreshes.
   *
   * @returns True if fresh tokens were obtained and stored, false otherwise (e.g. no custom
   * tokens, an expired refresh token, or a failing refresh call).
   */
  public async forceTokenRefresh(): Promise<boolean> {
    const store = useAppStore.getState();

    if (!store.customTokens) {
      return false;
    }

    // File-scoped sessions cannot refresh; callers must treat this as session end.
    if (store.customTokens.token && hasFileAccessScope(store.customTokens.token)) {
      return false;
    }

    return this.refreshWithDeduplication();
  }

  /**
   * Run a token refresh while de-duplicating concurrent callers: if a refresh is already in
   * flight, its promise is reused; otherwise a new one is started and cleared on completion.
   */
  private async refreshWithDeduplication(): Promise<boolean> {
    // If a refresh is already in progress, wait for it instead of starting a new one
    if (this.customTokenRefreshPromise) {
      return this.customTokenRefreshPromise;
    }

    // Start a new refresh operation and store the promise
    this.customTokenRefreshPromise = this.performCustomTokenRefresh();

    try {
      const result = await this.customTokenRefreshPromise;
      return result;
    } finally {
      // Clear the promise once the operation completes (success or failure)
      this.customTokenRefreshPromise = null;
    }
  }

  /**
   * Internal method to perform custom token refresh
   */
  private async performCustomTokenRefresh(): Promise<boolean> {
    try {
      const customTokens = useAppStore.getState().customTokens;

      // If refresh token is expired, can't refresh
      if (!customTokens || this.areCustomTokensExpired()) {
        console.warn('Custom refresh token is expired');
        return false;
      }

      // Call refresh token endpoint
      const response = await this.getUserService().refreshToken();

      // Extract expiry from new tokens
      const tokenExpiry = getJwtExpiration(response.token);
      const rTokenExpiry = getJwtExpiration(response.rToken);

      // Save new tokens with expiry timestamps
      useAppStore.getState().setCustomTokens({
        token: response.token,
        rToken: response.rToken,
        tokenExpiry: tokenExpiry || undefined,
        rTokenExpiry: rTokenExpiry || undefined,
      });

      // Also update the legacy token field for backward compatibility
      useAppStore.getState().setToken(response.token);

      return true;
    } catch (error) {
      console.warn('Failed to refresh custom tokens:', error);
      return false;
    }
  }

  private getUserService(): UserService {
    if (!this.userService) {
      this.userService = new UserService();
    }
    return this.userService;
  }

  /**
   * Writes access and refresh tokens from a completed login into the app store.
   *
   * @returns The new access token string.
   */
  private persistLoginTokens(response: { rToken: string; token: string }): string {
    const tokenExpiry = getJwtExpiration(response.token);
    const rTokenExpiry = getJwtExpiration(response.rToken);
    useAppStore.getState().setCustomTokens({
      token: response.token,
      rToken: response.rToken,
      tokenExpiry: tokenExpiry || undefined,
      rTokenExpiry: rTokenExpiry || undefined,
    });
    useAppStore.getState().setToken(response.token);
    return response.token;
  }

  /**
   * Check if user is authenticated (valid custom access token present).
   * Profile (`authenticatedUser`) may be unset in degraded/unavailable mode.
   */
  public isAuthenticated(): boolean {
    const store = useAppStore.getState();
    const accessToken = store.customTokens?.token;

    if (accessToken == null || accessToken === '') {
      return false;
    }

    if (this.areCustomTokensExpired()) {
      return false;
    }

    return true;
  }

  /**
   * Initialize authentication on app startup.
   * Clears tokens only on 401; other profile failures keep tokens so AuthContext
   * can enter unavailable/degraded mode without forcing logout.
   */
  public async initialize(): Promise<void> {
    const store = useAppStore.getState();

    if (store.customTokens?.token) {
      try {
        const user = await this.getUserService().getUserInfo();
        useAppStore.getState().setAuthenticatedUser(user);
      } catch (error) {
        if (isUnauthorizedHttpError(error)) {
          this.clearAllTokens();
        }
      }
    }
  }

  /**
   * Request OTP and handle provider logic
   */
  public async requestOtp(
    email: string,
    deviceInfo: any,
  ): Promise<{
    shouldUseDirectOAuth: boolean;
    oauthProviders?: OAuthProvider[];
    response?: any;
  }> {
    const payload = {
      culture: deviceInfo?.culture,
      username: email,
      offSet: deviceInfo?.offSetClientTime,
      tenantUrl: window.location.origin,
    };

    const response = await this.getUserService().requestUserOtp(payload);

    // Check if Direct OAuth is enabled
    if (response.oauthProviders && response.oauthProviders.length > 0) {
      return {
        shouldUseDirectOAuth: true,
        oauthProviders: response.oauthProviders,
        response,
      };
    }

    return {
      shouldUseDirectOAuth: false,
      response,
    };
  }

  /**
   * Discriminated results for `authenticateWithOtp`.
   * The `totpRequired` kind name is historical: the payload may represent TOTP, passkey, or either per policy.
   */
  public static readonly AUTH_OTP_RESULT = {
    success: (token: string) => ({ kind: 'success' as const, token }),
    totpRequired: (data: LoginV2TotpRequiredResponse) => ({
      kind: 'totpRequired' as const,
      expiresAt: data.expiresAt,
      partialAuthToken: data.partialAuthToken,
      passkeyAvailable: data.passkeyAvailable === true,
      totpAvailable: data.totpAvailable !== false,
      userEmail: data.userEmail,
    }),
    totpSetupRequired: (data: TotpSetupRequiredError) => ({
      kind: 'totpSetupRequired' as const,
      ...data,
    }),
  };

  /**
   * Validates the email OTP against `POST /user/auth/v2`.
   *
   * @returns Full session on success, partial auth + flags when a second factor is required,
   *   or (when the user has no TOTP yet and the company's grace period elapsed) a
   *   `403 TOTP_SETUP_REQUIRED` result carrying the partial token for the forced setup flow.
   */
  public async authenticateWithOtp(
    email: string,
    code: string | number,
  ): Promise<
    | { kind: 'success'; token: string }
    | {
        kind: 'totpRequired';
        expiresAt: string;
        partialAuthToken: string;
        passkeyAvailable: boolean;
        totpAvailable: boolean;
        userEmail: string;
      }
    | { kind: 'totpSetupRequired'; expiresAt: string; partialAuthToken: string; userEmail: string }
  > {
    const payload = {
      code,
      username: email,
      tenantUrl: window.location.origin,
    };

    let response: Awaited<ReturnType<UserService['login']>>;
    try {
      response = await this.getUserService().login(payload);
    } catch (error) {
      const totpSetupRequired = parseTotpSetupRequiredError(error);
      if (totpSetupRequired) {
        return AuthService.AUTH_OTP_RESULT.totpSetupRequired(totpSetupRequired);
      }
      throw error;
    }

    if (isLoginV2TwoFactorResponse(response)) {
      return AuthService.AUTH_OTP_RESULT.totpRequired(response);
    }

    const success = response as LoginV2SuccessResponse;
    this.persistLoginTokens(success);
    return AuthService.AUTH_OTP_RESULT.success(success.token);
  }

  /**
   * Verifies the mandatory (forced) TOTP setup code and completes auto-login.
   * Saves the new session tokens and returns the access token plus the one-time backup codes.
   */
  public async completeForcedTotpSetupVerify(
    partialAuthToken: string,
    code: string,
  ): Promise<{ token: string; backupCodes: string[] }> {
    const response = await this.getUserService().totpForcedSetupVerify({
      partialAuthToken,
      code: String(code),
    });
    const token = this.persistLoginTokens(response.auth);
    return { token, backupCodes: response.backupCodes };
  }

  /**
   * Complete login with TOTP or backup code after partial auth. Saves tokens and returns the access token.
   */
  public async completeLoginWithTotp(
    partialAuthToken: string,
    code: string,
    isBackupCode: boolean,
  ): Promise<string> {
    const response = await this.getUserService().loginWithTotp({
      partialAuthToken,
      code: String(code),
      isBackupCode,
    });
    return this.persistLoginTokens(response);
  }

  /**
   * Completes login after WebAuthn during the OTP + partial-auth (2FA) flow.
   *
   * @returns New access token after tokens are persisted.
   */
  public async completeLoginWithPasskeyAssertion(
    partialAuthToken: string,
    sessionId: string,
    assertionResponse: string,
  ): Promise<string> {
    const response = await this.getUserService().passkeyAuthComplete({
      assertionResponse,
      partialAuthToken,
      sessionId,
    });
    return this.persistLoginTokens(response);
  }

  /**
   * Completes passwordless login (no OTP) after WebAuthn assertion.
   *
   * @returns New access token after tokens are persisted.
   */
  public async completePasswordlessLogin(
    sessionId: string,
    assertionResponse: string,
  ): Promise<string> {
    const response = await this.getUserService().passkeyPasswordlessComplete({
      assertionResponse,
      sessionId,
    });
    return this.persistLoginTokens(response);
  }

  /**
   * Authenticate with OAuth callback (Direct OAuth flow)
   */
  public async authenticateWithOAuthCallback(
    provider: 'microsoft' | 'google',
    code: string,
    redirectUri: string,
    clientId: string,
    state: string,
  ): Promise<string> {
    const payload = {
      provider,
      clientId,
      code,
      redirectUri,
      tenantUrl: window.location.origin,
      state,
    };

    const response = await this.getUserService().oauthCallback(payload);

    // Extract expiry from tokens
    const tokenExpiry = getJwtExpiration(response.token);
    const rTokenExpiry = getJwtExpiration(response.rToken);

    // Save tokens with expiry timestamps in store
    useAppStore.getState().setCustomTokens({
      token: response.token,
      rToken: response.rToken,
      tokenExpiry: tokenExpiry || undefined,
      rTokenExpiry: rTokenExpiry || undefined,
    });

    // Also save in legacy token field for backward compatibility
    useAppStore.getState().setToken(response.token);

    return response.token;
  }

  /**
   * Logout.
   *
   * Invalidates the session on the server first (so the refresh token is revoked and can no
   * longer mint new tokens via /user/auth/rtoken), then clears local tokens. The server call is
   * best-effort: a network/HTTP failure must never block local cleanup, so tokens are always
   * cleared in the `finally` block.
   */
  public async logout(): Promise<void> {
    try {
      const customTokens = useAppStore.getState().customTokens;
      const accessToken = customTokens?.token;
      // File-scoped: end this session only — omit refreshToken / allDevices (ignored server-side).
      if (accessToken && hasFileAccessScope(accessToken)) {
        await this.getUserService().logout();
      } else {
        const rToken = customTokens?.rToken;
        // Only attempt the server call when we still hold a session to revoke.
        if (rToken) {
          await this.getUserService().logout(rToken);
        }
      }
    } catch (error) {
      console.warn('Server-side logout failed; clearing local session anyway:', error);
    } finally {
      this.clearAllTokens();
    }
  }

  /**
   * Clear all authentication tokens
   */
  private clearAllTokens(): void {
    const store = useAppStore.getState();
    store.setToken(null);
    store.setAuthenticatedUser(null);
    store.setCustomTokens(null);
  }

  /**
   * Get current authentication token
   */
  public getToken(): string | null {
    const store = useAppStore.getState();

    if (store.customTokens?.token) {
      return store.customTokens.token;
    }

    return null;
  }
}

export const authService = AuthService.getInstance();
